import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig } from 'axios';

import { isAxiosError } from 'axios';

import type { ServiceRequestConfig } from '~/services/createService';

import { hermes } from '~/services/serviceInstances';

export type HermesBaseQueryArg = {
  url: string;
  method?: AxiosRequestConfig['method'];
  body?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string | null | undefined>;
  rtkQueryOptions?: ServiceRequestConfig;
};

const compactHeaders = (
  headers?: Record<string, string | null | undefined>,
): Record<string, string> | undefined => {
  if (!headers) return undefined;
  const entries = Object.entries(headers).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

const normalizeUrl = (rawUrl: string) => {
  const withoutV1Prefix = rawUrl.replace(/^\/?v1/, '');
  return withoutV1Prefix.startsWith('/')
    ? withoutV1Prefix
    : `/${withoutV1Prefix}`;
};

export const hermesBaseQuery: BaseQueryFn<
  HermesBaseQueryArg,
  unknown,
  unknown
> = async ({ url, method = 'get', body, params, headers, rtkQueryOptions }) => {
  const path = normalizeUrl(url);
  const mergedHeaders = compactHeaders({
    ...(rtkQueryOptions?.headers as Record<string, string> | undefined),
    ...headers,
  });
  const config: ServiceRequestConfig = {
    showErrorToast: true,
    ...rtkQueryOptions,
    params,
    ...(mergedHeaders ? { headers: mergedHeaders } : {}),
  };

  try {
    let result: unknown;
    const m = String(method).toLowerCase();
    if (m === 'get') {
      result = await hermes.get(path, config);
    } else if (m === 'post') {
      result = await hermes.post(path, body, config);
    } else if (m === 'patch') {
      result = await hermes.patch(path, body, config);
    } else if (m === 'put') {
      result = await hermes.put(path, body, config);
    } else if (m === 'delete') {
      result = await hermes.remove(path, { ...config, data: body });
    } else {
      return { error: { status: 400, data: `Unsupported method: ${method}` } };
    }
    return { data: result };
  } catch (e) {
    if (isAxiosError(e)) {
      return {
        error: {
          status: e.response?.status ?? 'FETCH_ERROR',
          data: e.response?.data ?? e.message,
        },
      };
    }
    return { error: { status: 'FETCH_ERROR', error: String(e) } };
  }
};
